import type { VercelGeo } from "@/schemas";

// https://vercel.com/docs/headers/request-headers#x-vercel-ip-continent
const CONTINENT_NAMES: Record<string, string> = {
  AF: "Africa",
  AN: "Antarctica",
  AS: "Asia",
  EU: "Europe",
  NA: "North America",
  OC: "Oceania",
  SA: "South America",
};

/**
 * Derives geo data from Vercel's x-vercel-ip-* request headers.
 * Returns null when the country header is absent (e.g. local dev).
 */
export function geoFromHeaders(headers: Headers): VercelGeo | null {
  const countryCode = normalizeCode(headers.get("x-vercel-ip-country"));
  if (!countryCode) {
    return null;
  }
  const continentCode = normalizeCode(headers.get("x-vercel-ip-continent"));
  return {
    countryCode,
    countryName: countryDisplayName(countryCode),
    continentCode,
    continentName: continentCode
      ? (CONTINENT_NAMES[continentCode] ?? null)
      : null,
    cityName: decodeCity(headers.get("x-vercel-ip-city")),
    subdivisionCode: nonEmpty(headers.get("x-vercel-ip-country-region")),
    latitude: parseCoordinate(headers.get("x-vercel-ip-latitude")),
    longitude: parseCoordinate(headers.get("x-vercel-ip-longitude")),
    timeZone: nonEmpty(headers.get("x-vercel-ip-timezone")),
    postalCode: nonEmpty(headers.get("x-vercel-ip-postal-code")),
  };
}

/**
 * Maps geo data to PostHog GeoIP event property names, matching what
 * PostHog's server-side GeoIP transformation would set. $geoip_disable
 * prevents any server-side transformation from clobbering these values.
 */
export function geoEventProperties(
  geo: VercelGeo
): Record<string, string | number | boolean> {
  return {
    $geoip_disable: true,
    $geoip_country_code: geo.countryCode,
    ...(geo.countryName != null && { $geoip_country_name: geo.countryName }),
    ...(geo.continentCode != null && {
      $geoip_continent_code: geo.continentCode,
    }),
    ...(geo.continentName != null && {
      $geoip_continent_name: geo.continentName,
    }),
    ...(geo.cityName != null && { $geoip_city_name: geo.cityName }),
    ...(geo.subdivisionCode != null && {
      $geoip_subdivision_1_code: geo.subdivisionCode,
    }),
    ...(geo.latitude != null && { $geoip_latitude: geo.latitude }),
    ...(geo.longitude != null && { $geoip_longitude: geo.longitude }),
    ...(geo.timeZone != null && { $geoip_time_zone: geo.timeZone }),
    ...(geo.postalCode != null && { $geoip_postal_code: geo.postalCode }),
  };
}

function normalizeCode(value: string | null): string | null {
  const code = value?.trim().toUpperCase();
  return code && /^[A-Z]{2}$/.test(code) ? code : null;
}

function countryDisplayName(code: string): string | null {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) ?? null;
  } catch {
    return null;
  }
}

// x-vercel-ip-city encodes non-ASCII characters per RFC3986
function decodeCity(value: string | null): string | null {
  const city = nonEmpty(value);
  if (!city) {
    return null;
  }
  try {
    return decodeURIComponent(city);
  } catch {
    return city;
  }
}

function parseCoordinate(value: string | null): number | null {
  if (!value) {
    return null;
  }
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function nonEmpty(value: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}
